import ServiceLocationsEditor from "@/components/ServiceLocationsEditor";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X, Loader2, Library, MapPin } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import ImageUpload from "@/components/ImageUpload";
import RichTextEditor from "@/components/RichTextEditor";
import { toast } from "sonner";

const CATEGORIES = ["Wellness", "Float Therapy", "Massage", "Reiki", "Energy Healing", "Sound Healing", "Breathwork", "Coaching", "Meditation", "Movement", "Workshop"];

const HOST_BADGE = {
  pending: { text: "Pending center approval", cls: "bg-[#D98E73]/15 text-[#B5794E]" },
  approved: { text: "Approved for in-person", cls: "bg-[#5C8A72]/15 text-[#5C8A72]" },
  rejected: { text: "Not approved — online only", cls: "bg-red-100 text-red-600" },
};

export default function ServiceFormModal({ service, locations = [], onClose, onSaved }) {
  const editing = !!service?.id;
  const { data: catalog = [] } = useQuery({
    queryKey: ["center-services-library"],
    queryFn: () => api.get("/center-services").then((r) => r.data),
  });
  const { data: hostCenters = [] } = useQuery({
    queryKey: ["enh-host-centers"],
    queryFn: () => api.get("/enhancer/me/host-centers").then((r) => r.data.centers),
  });
  const [form, setForm] = useState({
    title: service?.title || "", category: service?.category || "Wellness",
    short_description: service?.short_description || "", description: service?.description || "",
    duration_min: service?.duration_min || 60, price: service?.price ?? 0,
    member_discount_pct: service?.member_discount_pct || 0,
    location_id: service?.location_id || "", image: service?.image || "",
    requires_agreement: service?.requires_agreement || false,
    buffer_before_min: service?.buffer_before_min || 0,
    buffer_after_min: service?.buffer_after_min || 0,
    delivery_method: service?.delivery_method || "in_person",
    center_host_location_id: service?.center_host_location_id || "",
    published: service?.published ?? true,
  });
  const [svcLocs, setSvcLocs] = useState((service?.service_locations || []).filter(
    (l) => !(service?.center_host_status === "approved" && String(l.location_id) === String(service?.center_host_location_id))));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const LOC_OPTIONS = [
    { id: "virtual", name: "Virtual / Online", mode: "virtual" },
    ...hostCenters.map((c) => ({ id: c.id, name: `${c.name}${c.city ? ` — ${c.city}` : ""}`, mode: "in_person" })),
    ...locations.filter((l) => !hostCenters.some((c) => c.id === l.id)).map((l) => ({ id: l.id, name: `${l.name} (own space)`, mode: "in_person" })),
  ];
  const addLoc = () => setSvcLocs([...svcLocs, { location_id: "virtual", name: "Virtual / Online", mode: "virtual", price: "", room: "" }]);
  const updLoc = (i, k, v) => setSvcLocs(svcLocs.map((r, idx) => {
    if (idx !== i) return r;
    const next = { ...r, [k]: v };
    if (k === "location_id") {
      const opt = LOC_OPTIONS.find((o) => o.id === v);
      next.name = opt?.name || v; next.mode = opt?.mode || "in_person";
    }
    return next;
  }));
  const rmLoc = (i) => setSvcLocs(svcLocs.filter((_, idx) => idx !== i));

  const adoptCatalog = (id) => {
    const c = catalog.find((x) => x.id === id);
    if (!c) return;
    setForm((f) => ({
      ...f,
      title: f.title || c.title,
      category: c.category || f.category,
      short_description: f.short_description || c.short_description || "",
      description: f.description || c.description,
      duration_min: c.duration_min || f.duration_min,
    }));
    toast.success(`Started from “${c.title}” — edit anything below before saving.`);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const payload = { ...form, price: parseFloat(form.price) || 0, duration_min: parseInt(form.duration_min) || 60, member_discount_pct: parseInt(form.member_discount_pct) || 0,
      buffer_before_min: parseInt(form.buffer_before_min) || 0, buffer_after_min: parseInt(form.buffer_after_min) || 0,
      service_locations: svcLocs.map((r) => ({ location_id: r.location_id, name: r.name, mode: r.mode, price: r.price === "" ? null : parseFloat(r.price), room: r.room || "" })) };
    try {
      if (editing) await api.put(`/enhancer/services/${service.id}`, payload);
      else await api.post("/enhancer/services", payload);
      toast.success(
        form.published
          ? (editing ? "Service updated & live on your profile." : "Service published to your profile.")
          : (editing ? "Service saved as a draft (hidden)." : "Service saved as a draft (hidden).")
      );
      onSaved();
      onClose();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center px-4 bg-black/40 backdrop-blur-sm overflow-y-auto py-10" data-testid="service-form-modal">
      <div className="relative w-full max-w-lg bg-[#F9F7F3] rounded-3xl shadow-2xl p-8 my-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#4A5D6B] hover:text-[#144F75]"><X size={20} /></button>
        <h3 className="font-serif text-2xl text-[#1A242B] mb-5">{editing ? "Edit service" : "New service"}</h3>
        <form onSubmit={submit} className="space-y-3">
          {!editing && catalog.length > 0 && (
            <div className="rounded-xl border border-[#144F75]/20 bg-[#144F75]/[0.04] p-3" data-testid="svc-catalog-adopt-box">
              <label className="text-xs font-semibold text-[#144F75] mb-1 flex items-center gap-1.5"><Library size={13} /> Start from the service library <span className="font-normal text-[#8FA89B]">(optional)</span></label>
              <select defaultValue="" onChange={(e) => { if (e.target.value) adoptCatalog(e.target.value); e.target.value = ""; }} data-testid="svc-catalog-select" className="eyw-input w-full text-sm">
                <option value="">Choose a shared service to pre-fill…</option>
                {catalog.map((c) => <option key={c.id} value={c.id}>{c.title}{c.category ? ` · ${c.category}` : ""}</option>)}
              </select>
              <p className="text-[11px] text-[#8FA89B] mt-1">Adopting only pre-fills the fields — you can edit everything, and it stays your own offering.</p>
            </div>
          )}
          <input required placeholder="Service title" value={form.title} onChange={set("title")} data-testid="svc-title" className="eyw-input w-full" />
          <div className="grid grid-cols-2 gap-3">
            <input list="svc-cat-list" value={form.category} onChange={set("category")} placeholder="Category (pick or type your own)" data-testid="svc-category" className="eyw-input w-full" />
            <datalist id="svc-cat-list">{CATEGORIES.map((c) => <option key={c} value={c} />)}</datalist>
            <select value={form.location_id} onChange={set("location_id")} data-testid="svc-location" className="eyw-input w-full">
              <option value="">EYW location (70/30)</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.name} ({l.type})</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><label className="text-xs text-[#4A5D6B]">Price $</label><input type="number" step="0.01" value={form.price} onChange={set("price")} data-testid="svc-price" className="eyw-input w-full" /></div>
            <div><label className="text-xs text-[#4A5D6B]">Duration (min)</label><input type="number" value={form.duration_min} onChange={set("duration_min")} data-testid="svc-duration" className="eyw-input w-full" /></div>
            <div><label className="text-xs text-[#4A5D6B]">Member % off</label><input type="number" value={form.member_discount_pct} onChange={set("member_discount_pct")} data-testid="svc-discount" className="eyw-input w-full" /></div>
          </div>
          <div>
            <label className="text-xs text-[#4A5D6B]">Delivery method</label>
            <select value={form.delivery_method} onChange={set("delivery_method")} data-testid="svc-delivery-method" className="eyw-input w-full">
              <option value="in_person">In person</option>
              <option value="zoom">Virtual — Zoom (your connected account)</option>
              <option value="google_meet">Virtual — Google Meet (via Google Calendar)</option>
              <option value="manual">Virtual — I'll share the link manually</option>
            </select>
          </div>
          <div className="rounded-xl border border-[#5C8A72]/25 bg-[#5C8A72]/[0.05] p-3" data-testid="svc-host-center-box">
            <label className="text-xs font-semibold text-[#3E6B54] mb-1 flex items-center gap-1.5"><MapPin size={13} /> Host in person at an EYW center? <span className="font-normal text-[#8FA89B]">(optional — needs the center's approval)</span></label>
            <select value={form.center_host_location_id} onChange={set("center_host_location_id")} data-testid="svc-host-center-select" className="eyw-input w-full text-sm">
              <option value="">Online / virtual only</option>
              {hostCenters.map((c) => <option key={c.id} value={c.id}>{c.name}{c.city ? ` — ${c.city}` : ""}</option>)}
            </select>
            {editing && service?.center_host_status && HOST_BADGE[service.center_host_status] && (
              <div className="mt-2" data-testid="svc-host-status">
                <span className={`inline-block text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${HOST_BADGE[service.center_host_status].cls}`}>{HOST_BADGE[service.center_host_status].text}</span>
                {service.center_host_status === "rejected" && service.center_host_note && <p className="text-[11px] text-red-600 mt-1">Center note: {service.center_host_note}</p>}
              </div>
            )}
            {hostCenters.length === 0 && <p className="text-[11px] text-[#8FA89B] mt-1">You can request in-person hosting once you're approved at a center (Our Centers → apply).</p>}
            {form.center_host_location_id && <p className="text-[11px] text-[#8FA89B] mt-1">The center will review this. Until approved, it stays online/virtual on your profile.</p>}
          </div>
          <input placeholder="Short description" value={form.short_description} onChange={set("short_description")} data-testid="svc-short" className="eyw-input w-full" />
          <div>
            <label className="text-xs text-[#4A5D6B] mb-1 block">Full description</label>
            <RichTextEditor value={form.description} onChange={(html) => setForm((f) => ({ ...f, description: html }))} testid="svc-desc" enableBlocks={false} placeholder="Describe this service — add headings, lists, images and video…" minHeight={140} />
          </div>
          <ImageUpload value={form.image} onChange={(url) => setForm((f) => ({ ...f, image: url }))} label="Service photo (square)" crop="square" testid="svc-image" />

          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-[#4A5D6B]">Buffer before (min)</label><input type="number" min={0} value={form.buffer_before_min} onChange={set("buffer_before_min")} data-testid="svc-buffer-before" className="eyw-input w-full" /></div>
            <div><label className="text-xs text-[#4A5D6B]">Buffer / cleaning after (min)</label><input type="number" min={0} value={form.buffer_after_min} onChange={set("buffer_after_min")} data-testid="svc-buffer-after" className="eyw-input w-full" /></div>
          </div>

          {!editing && (
          <div className="rounded-xl border border-[#E5E0D8] p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-[#1A242B]">Offered at (locations & modes)</span>
              <button type="button" onClick={addLoc} data-testid="svc-add-location" className="text-xs text-[#144F75] font-medium">+ Add location</button>
            </div>
            <p className="text-[11px] text-[#8FA89B] mb-2">Leave empty to use the default location above. Add rows to offer this virtually and/or at multiple locations with optional per-location pricing.</p>
            {svcLocs.map((r, i) => (
              <div key={i} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center mb-2" data-testid={`svc-loc-row-${i}`}>
                <select value={r.location_id} onChange={(e) => updLoc(i, "location_id", e.target.value)} className="eyw-input text-sm">
                  {LOC_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
                <input type="number" step="0.01" placeholder="Price" value={r.price} onChange={(e) => updLoc(i, "price", e.target.value)} className="eyw-input text-sm w-20" title="Price override (blank = default)" />
                <input placeholder="room key" value={r.room} onChange={(e) => updLoc(i, "room", e.target.value)} className="eyw-input text-sm w-24" title="Room key for conflict blocking (in-person)" />
                <button type="button" onClick={() => rmLoc(i)} className="text-red-500 text-xs px-1">✕</button>
              </div>
            ))}
          </div>
          )}

          <label className="flex items-center gap-2 text-sm text-[#4A5D6B]">
            <input type="checkbox" checked={form.requires_agreement} onChange={set("requires_agreement")} /> Requires a signed waiver/agreement
          </label>
          <label className="flex items-center justify-between rounded-xl border border-[#E5E0D8] px-3.5 py-2.5 bg-white">
            <span className="text-sm font-medium text-[#1A242B]">Publish to my public profile
              <span className="block text-[11px] font-normal text-[#8FA89B]">On = visible & bookable. Off = saved as a private draft.</span>
            </span>
            <button type="button" onClick={() => setForm((f) => ({ ...f, published: !f.published }))} data-testid="svc-published"
              className={`relative h-7 w-12 rounded-full transition-colors shrink-0 ${form.published ? "bg-[#5C8A72]" : "bg-[#E5E0D8]"}`}>
              <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${form.published ? "left-6" : "left-1"}`} />
            </button>
          </label>
          {editing ? <ServiceLocationsEditor serviceId={service.id} testid="enh-svc-locations" />
            : <p className="text-xs text-[#8FA89B]">After creating the service you can add more centers and Online here.</p>}
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button type="submit" disabled={loading} data-testid="svc-save" className="eyw-btn w-full disabled:opacity-60">
            {loading && <Loader2 className="animate-spin" size={18} />} {editing ? "Save changes" : "Create service"}
          </button>
        </form>
      </div>

    </div>
  );
}
